import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Importamos useNavigate
import {
  FolderIcon,
  EnvelopeIcon,
  PlusIcon,
  NewspaperIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/solid";
import styles from '../styles/Dashboard.module.css'; 
import AdminLayout from '../components/layout/AdminLayout'; 
import { useToast } from '../hooks/useToast'; // Importamos el Toast para alertas

// Importar servicios y tipos
import { getProjects } from '../services/ProjectService';
import { getInquiries } from '../services/InquiryService';
import { getArticles } from '../services/ArticleService';
import { getUnreadCount as getUnreadQuotesCount } from '../services/QuoteService';
import { IProject } from '../types/Project';
import { IInquiry } from '../types/Inquiry';
import { IArticle } from '../types/Article';

// --- Definición de Tipos ---
interface Stat {
    id: number;
    title: string;
    value: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    color: string;
}

interface RecentActivityItem {
    id: number | string;
    type: 'project' | 'article' | 'message' | 'quote';
    title: string;
    description: string;
    time: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    colorClass: 'project' | 'client' | 'message' | 'quote';
}

// --- Funciones de Lógica (Sin cambios) ---
const generateStats = (
    projects: IProject[], 
    inquiries: IInquiry[],
    articles: IArticle[],
    unreadQuotes: number
): Stat[] => {
    const safeProjects = projects || [];
    const safeInquiries = inquiries || [];
    const safeArticles = articles || [];
    const unreadMessages = safeInquiries.filter(i => i?.isRead === false).length;

    return [
        { id: 1, title: "Total Proyectos", value: safeProjects.length.toString(), icon: FolderIcon, color: "projects" },
        { id: 2, title: "Artículos Publicados", value: safeArticles.length.toString(), icon: NewspaperIcon, color: "clients" },
        { id: 3, title: "Mensajes Sin Leer", value: unreadMessages.toString(), icon: EnvelopeIcon, color: "messages" },
        { id: 4, title: "Cotizaciones Pendientes", value: unreadQuotes.toString(), icon: DocumentTextIcon, color: "quotes" },
    ];
};

const mapAndCombineActivity = (
    projects: IProject[], 
    inquiries: IInquiry[],
    articles: IArticle[]
): RecentActivityItem[] => {
    const safeProjects = projects || [];
    const safeInquiries = inquiries || [];
    const safeArticles = articles || [];

    const projectActivities: RecentActivityItem[] = safeProjects.map(p => ({
        id: p?.id || 0, type: 'project', title: "Proyecto Actualizado", description: p?.title || 'Sin título',
        time: p?.id?.toString() || '0', icon: FolderIcon, colorClass: 'project',
    }));
    
    const articleActivities: RecentActivityItem[] = safeArticles.map(a => ({
        id: a?.id || 0, type: 'article', title: "Artículo Publicado", description: a?.title || 'Sin título',
        time: a?.publishedAt || new Date().toISOString(), icon: NewspaperIcon, colorClass: 'client',
    }));

    const messageActivities: RecentActivityItem[] = safeInquiries.map(i => ({
        id: i?.id || 0, type: 'message', title: i?.isQuote ? "Nueva Cotización" : "Nuevo Mensaje",
        description: `${i?.name || 'Sin nombre'} / ${i?.subject || 'Sin asunto'}`,
        time: i?.receivedAt || new Date().toISOString(), icon: EnvelopeIcon, colorClass: 'message',
    }));

    const allActivities = [...projectActivities, ...articleActivities, ...messageActivities];
    allActivities.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()); // Simple sort
    
    return allActivities.slice(0, 5).map(item => {
        let formattedTime = 'Reciente';
        try { formattedTime = new Date(item.time).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }); } catch (e) {}
        return { ...item, time: formattedTime };
    });
};

const DashboardContent: React.FC = () => {
    const [stats, setStats] = useState<Stat[]>([]);
    const [recentActivities, setRecentActivities] = useState<RecentActivityItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const navigate = useNavigate(); // Para navegar manualmente
    const toast = useToast(); // Para mostrar alertas

    // --- LÓGICA DE PERMISOS ---
    const userRole = localStorage.getItem('userRole') || 'observador';
    const canCreate = userRole === 'administrador' || userRole === 'editor';

    // Función para manejar clics en acciones protegidas
    const handleCreateAction = (path: string) => {
        if (!canCreate) {
            toast.warning('Acceso Restringido', 'Tu rol de "Observador" no permite crear contenido.');
            return;
        }
        navigate(path);
    };

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const [projects, inquiries, articles, unreadQuotes] = await Promise.all([
                    getProjects().catch(() => []),
                    getInquiries().catch(() => []),
                    getArticles().catch(() => []),
                    getUnreadQuotesCount().catch(() => 0),
                ]);

                setStats(generateStats(projects, inquiries, articles, unreadQuotes));
                setRecentActivities(mapAndCombineActivity(projects, inquiries, articles));
                setError(null);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []); 
    
    if (loading) return <div className={styles.loadingContainer}><div className={styles.loadingSpinner}></div><p>Cargando...</p></div>;
    if (error) return <div className={styles.errorContainer}>{error}</div>;

    return (
        <>
            {/* Header */}
            <div className={styles.mainContentHeader}>
                <h1 className={styles.headerTitle}>Dashboard Principal</h1>
                <div className={styles.headerActions}>
                    <Link to="/messages" style={{ textDecoration: 'none' }}>
                        <button className={styles.secondaryButton}>
                            <EnvelopeIcon style={{ width: '1rem', height: '1rem' }} />
                            Mensajes
                        </button>
                    </Link>
                    
                    {/* Botón Protegido: Nuevo Proyecto */}
                    <button 
                        className={styles.primaryButton}
                        onClick={() => handleCreateAction('/projects/new')}
                    >
                        <PlusIcon style={{ width: '1rem', height: '1rem' }} />
                        Nuevo Proyecto
                    </button>
                </div>
            </div>

            {/* Estadísticas */}
            <div className={styles.statsGrid}>
                {stats.map((stat) => (
                    <div key={stat.id} className={`${styles.statCard} ${styles[stat.color]}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flexStart' }}>
                            <div>
                                <div className={styles.statLabel}>{stat.title}</div>
                                <div className={styles.statValue}>{stat.value}</div>
                            </div>
                            <stat.icon style={{ width: '2rem', height: '2rem', opacity: 0.5 }} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Actividad Reciente */}
            <div className={styles.recentActivity}>
                <h3 className={styles.chartTitle}>Actividad Reciente</h3>
                <div className={styles.activityList}>
                    {recentActivities.length > 0 ? (
                        recentActivities.map((activity) => (
                            <div key={`${activity.type}-${activity.id}`} className={styles.activityItem}>
                                <div className={`${styles.activityIcon} ${styles[activity.colorClass]}`}>
                                    <activity.icon style={{ width: '1.25rem', height: '1.25rem' }} />
                                </div>
                                <div className={styles.activityContent}>
                                    <div className={styles.activityTitle}>{activity.title}</div>
                                    <div className={styles.activityDescription}>{activity.description}</div>
                                </div>
                                <div className={styles.activityTime}>{activity.time}</div>
                            </div>
                        ))
                    ) : (
                        <p className={styles.noProjects} style={{ padding: '0' }}>No hay actividad reciente.</p>
                    )}
                </div>
            </div>

            {/* Acciones Rápidas */}
            <div>
                <h3 className={styles.chartTitle}>Acciones Rápidas</h3>
                <div className={styles.quickActionsGrid}>
                    
                    {/* Tarjeta Protegida: Nuevo Proyecto */}
                    <div 
                        className={styles.actionCard} 
                        onClick={() => handleCreateAction('/projects/new')}
                        // Añadimos style cursor para que se sienta clickeable
                        style={{ cursor: 'pointer' }} 
                    >
                        <div className={styles.actionIcon}>
                            <PlusIcon style={{ width: '1.5rem', height: '1.5rem' }} />
                        </div>
                        <div className={styles.actionTitle}>Nuevo Proyecto</div>
                        <div className={styles.actionDescription}>Crear nuevo proyecto arquitectónico</div>
                    </div>

                    {/* Tarjeta Protegida: Nuevo Artículo */}
                    <div 
                        className={styles.actionCard}
                        onClick={() => handleCreateAction('/articles/new')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className={styles.actionIcon} style={{ backgroundColor: '#10b981' }}>
                            <NewspaperIcon style={{ width: '1.5rem', height: '1.5rem' }} />
                        </div>
                        <div className={styles.actionTitle}>Nuevo Artículo</div>
                        <div className={styles.actionDescription}>Escribir una noticia o artículo</div>
                    </div>

                    {/* Tarjeta Pública: Mensajes (Observadores pueden ver) */}
                    <Link to="/messages" className={styles.actionCard}>
                        <div className={styles.actionIcon} style={{ backgroundColor: '#f59e0b' }}>
                            <EnvelopeIcon style={{ width: '1.5rem', height: '1.5rem' }} />
                        </div>
                        <div className={styles.actionTitle}>Mensajes</div>
                        <div className={styles.actionDescription}>Revisar consultas</div>
                    </Link>

                    {/* Tarjeta Pública: Cotizaciones (Observadores pueden ver) */}
                    <Link to="/quotes" className={styles.actionCard}>
                        <div className={styles.actionIcon} style={{ backgroundColor: '#8b5cf6' }}>
                            <DocumentTextIcon style={{ width: '1.5rem', height: '1.5rem' }} />
                        </div>
                        <div className={styles.actionTitle}>Cotizaciones</div>
                        <div className={styles.actionDescription}>Revisar solicitudes</div>
                    </Link>
                </div>
            </div>
        </>
    );
}

const DashboardPage: React.FC = () => (
    <AdminLayout pageTitle="Dashboard Principal">
        <DashboardContent />
    </AdminLayout>
);

export default DashboardPage;